import { FeedbackMetadataKeyValues } from '@modules/feedback/components/FeedbackCard/FeedbackMetadataKeyValues';
import { FeedbackMetadataField } from '@modules/feedback/types';
import { trackFeedbackMetadataEvent } from '@modules/feedback/utils';
import { useIsFeedbackRedesignEnabled } from '@modules/feedbackV2/common/hooks';
import { ExpandedFeedbackMetadataGrid } from '@modules/feedbackV2/expanded/components/ExpandedFeedbackMetadataGrid';
import { KeyboardArrowDownRounded } from '@mui/icons-material';
import { Box, Button, Grid, Typography } from '@mui/material';
import { ObjectType } from 'generated/graphql';
import { useState } from 'react';

const DEFAULT_METADATA_FIELDS_TO_DISPLAY = 4;

interface FeedbackMetadataGridProps {
  metadataFields: FeedbackMetadataField[];
  shouldLimitGridItems?: boolean;
  disablePadding?: boolean;
  gridColumns?: number;
  feedbackID: string;
  objectType: ObjectType;
}

export const FeedbackMetadataGrid = ({
  metadataFields,
  shouldLimitGridItems,
  disablePadding = false,
  gridColumns = 2,
  feedbackID,
  objectType,
}: FeedbackMetadataGridProps) => {
  const [shouldShowAll, setShouldShowAll] = useState(false);

  const isFeedbackRedesignEnabled = useIsFeedbackRedesignEnabled();

  if (isFeedbackRedesignEnabled) {
    return (
      <ExpandedFeedbackMetadataGrid
        metadataFields={metadataFields}
        feedbackID={feedbackID}
      />
    );
  }

  const metadataFieldsToDisplay =
    shouldShowAll || !shouldLimitGridItems
      ? metadataFields
      : metadataFields.slice(0, DEFAULT_METADATA_FIELDS_TO_DISPLAY);

  const shouldRenderShowAllButton =
    shouldLimitGridItems &&
    metadataFields.length > DEFAULT_METADATA_FIELDS_TO_DISPLAY;

  return (
    <>
      <Box p={disablePadding ? 0 : 3}>
        {metadataFieldsToDisplay.length > 0 ? (
          <Grid container spacing={4} columns={gridColumns}>
            {metadataFieldsToDisplay.map((metadataField) => {
              const shouldAddOpenInSearchLink =
                metadataField.isReference &&
                (metadataField.objectType === ObjectType.User ||
                  metadataField.objectType === ObjectType.Account);

              const isMultiValueMetadataField = metadataField.values.length > 1;

              return (
                <Grid
                  key={`${metadataField.key}_${metadataField.objectID}`}
                  item
                  md={isMultiValueMetadataField ? gridColumns : 1}
                >
                  <FeedbackMetadataKeyValues
                    displayName={metadataField.key}
                    values={metadataField.values}
                    metadataValue={metadataField.metadataValue}
                    metadataFieldKey={metadataField.metadataFieldKey}
                    shouldAddOpenInSearchLink={shouldAddOpenInSearchLink}
                    feedbackID={feedbackID}
                  />
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Typography variant="caption" color="text.secondary">
            No metadata found.
          </Typography>
        )}
      </Box>
      {shouldRenderShowAllButton && (
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            color: 'text.primary',
            px: 3,
            py: 1,
          }}
        >
          <Button
            size="small"
            color="inherit"
            sx={{
              cursor: 'pointer',
              textTransform: 'none',
            }}
            onClick={() => {
              setShouldShowAll((prev) => !prev);
              trackFeedbackMetadataEvent({
                eventName: shouldShowAll
                  ? 'Feedback Metadata - Show Less Button Clicked'
                  : 'Feedback Metadata - Show All Button Clicked',
                feedbackID,
                objectType,
              });
            }}
            endIcon={
              <Box
                sx={{
                  transform: shouldShowAll ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.4s ease',
                }}
              >
                <KeyboardArrowDownRounded />
              </Box>
            }
          >
            {shouldShowAll ? 'Show Less' : 'Show All'}
          </Button>
        </Box>
      )}
    </>
  );
};
